import { markRaw, type Component } from "vue";
import {
  LandmarkIcon,
  MonitorIcon,
  SettingsIcon,
  StoreIcon,
  VaultIcon,
} from "@smeetz/ui-icons";
import { CashLocationDeviceType } from "@/constants/cash-location";

export interface CashLocationVisual {
  icon: Component;
  bgClass: string;
  colorClass: string;
}

/**
 * Icon + colours for a cash-location device type. Mirrors the back-office
 * "Device type" picker so every cashbook UI reads the same:
 * POS = monitor, Kiosk = storefront, Safe = vault, Bank = landmark, Other = gear.
 *
 * Single source of truth — imported by the cash-transfer, cash-float open,
 * close-confirm and device-settings screens so their icons never drift apart.
 */
export function locationVisualFor(
  deviceType: CashLocationDeviceType | null | undefined
): CashLocationVisual {
  switch (deviceType) {
    case CashLocationDeviceType.SAFE:
      return {
        icon: markRaw(VaultIcon),
        bgClass: "bg-purple-50",
        colorClass: "text-purple-600",
      };
    case CashLocationDeviceType.BANK:
      return {
        icon: markRaw(LandmarkIcon),
        bgClass: "bg-emerald-50",
        colorClass: "text-emerald-700",
      };
    case CashLocationDeviceType.KIOSK:
      return {
        icon: markRaw(StoreIcon),
        bgClass: "bg-orange-50",
        colorClass: "text-orange-600",
      };
    case CashLocationDeviceType.POS:
      return {
        icon: markRaw(MonitorIcon),
        bgClass: "bg-teal-50",
        colorClass: "text-teal-700",
      };
    // OTHER is the catch-all bucket; it mirrors the picker's "Other" gear.
    case CashLocationDeviceType.OTHER:
    default:
      return {
        icon: markRaw(SettingsIcon),
        bgClass: "bg-teal-50",
        colorClass: "text-teal-700",
      };
  }
}
